/**
 * #989 — explicit three-venue labels for any surface that shows Polymarket US
 * next to Kalshi or international Polymarket.
 *
 * Every venue mark reads its name, letters and tone from `@/lib/venues`, as
 * `VenueChip` (src/components/atoms/VenueChip.tsx) now does for all three
 * venues. The contract (docs/260926PolymarketUsFrontend.md, September 28
 * decisions) requires an explicit mapping before US rows are mounted.
 */
import { VENUE_NAME, VENUE_SHORT, VENUE_TONE, type TradingVenue } from '@/lib/venues';

export type PortfolioVenue = TradingVenue;
export { VENUE_NAME };

/** Badge matching VenueChip's size, for all three venues. */
export function PortfolioVenueBadge({ venue }: { venue: PortfolioVenue }) {
  return (
    <span
      className={`${VENUE_TONE[venue]} rounded px-1.5 py-0.5 text-[10px] font-bold`}
      title={VENUE_NAME[venue]}
      role="img"
      aria-label={VENUE_NAME[venue]}
    >
      {VENUE_SHORT[venue]}
    </span>
  );
}

/**
 * #1018 — the one name a trader sees for a Polymarket US account, on the
 * Game Detail ticket and in personal Settings alike. Accounts have no
 * human-readable name, so they are numbered in the relay's list order among
 * the accounts the trader can view as their own; both surfaces number the
 * same list, so "Account 2" is the same account on each.
 */
export function usAccountLabel(ownership: 'personal' | 'pooled', index: number, count: number): string {
  if (count > 1) return `Account ${index + 1} · ${ownership === 'pooled' ? 'desk' : 'personal'}`;
  return ownership === 'pooled' ? 'Desk account' : 'Personal account';
}

/** #1018 — the US reason vocabulary now lives with the other US rules; kept
 * importable from here for the surfaces that already use it. */
export { usReason, usReasonStatus } from '@/lib/polymarketUs/reason';
